// Confirmación de un cambio de estado. Si el dominio lo pide, agrega un motivo opcional u obligatorio.
import { useId, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '@/kit';
import { FIELD_INPUT, FIELD_LABEL } from '@/shared/patterns/EditorForm';

export interface LifecycleConfirmation {
    title: ReactNode;
    description?: ReactNode;
    confirmLabel: string;
    destructive?: boolean;
    reason?: {
        required: boolean;
        placeholder?: string;
        /** Para qué queda el motivo; va bajo el campo. */
        hint?: string;
    };
}

export interface LifecycleConfirmDialogProps {
    /** `null` = cerrado. */
    confirmation: LifecycleConfirmation | null;
    loading?: boolean;
    onCancel: () => void;
    /** El motivo llega recortado, y solo si se escribió. */
    onConfirm: (reason?: string) => void;
}

export function LifecycleConfirmDialog({ confirmation, ...rest }: LifecycleConfirmDialogProps) {
    if (confirmation === null) return null;
    return <OpenConfirmDialog confirmation={confirmation} {...rest} />;
}

function OpenConfirmDialog({
    confirmation,
    loading = false,
    onCancel,
    onConfirm,
}: LifecycleConfirmDialogProps & { confirmation: LifecycleConfirmation }) {
    const [reason, setReason] = useState('');
    const fieldId = useId();
    const hintId = useId();
    const trimmed = reason.trim();
    const missingReason = confirmation.reason?.required === true && trimmed === '';

    return (
        <ConfirmDialog
            open
            onOpenChange={(open) => {
                if (!open) onCancel();
            }}
            title={confirmation.title}
            description={confirmation.description}
            confirmLabel={confirmation.confirmLabel}
            destructive={confirmation.destructive}
            loading={loading}
            confirmDisabled={missingReason}
            onConfirm={() => {
                if (missingReason) return;
                onConfirm(confirmation.reason && trimmed !== '' ? trimmed : undefined);
            }}
        >
            {confirmation.reason ? (
                <div>
                    <label htmlFor={fieldId} className={FIELD_LABEL}>
                        {confirmation.reason.required ? 'Motivo' : 'Motivo (opcional)'}
                    </label>
                    <textarea
                        id={fieldId}
                        rows={2}
                        className={FIELD_INPUT}
                        value={reason}
                        placeholder={confirmation.reason.placeholder}
                        aria-describedby={missingReason ? hintId : undefined}
                        onChange={(event) => {
                            setReason(event.target.value);
                        }}
                    />
                    {missingReason ? (
                        <span id={hintId} className="mt-1 block text-[10px] text-destructive">
                            Escribe el motivo para continuar
                        </span>
                    ) : null}
                    {confirmation.reason.hint ? (
                        <span className="mt-1 block text-[10px] text-muted-foreground">
                            {confirmation.reason.hint}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </ConfirmDialog>
    );
}
